import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/common/Logo';
import { ROUTES } from '@/constants/app';
import { Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AFFICHE_DEMO,
  EXEMPLES_SANS_VIDEO,
  VIDEO_DEMO,
} from './annonce.constants';

interface Props {
  /** Appelé au clic : enregistre la validation, puis libère l'écran. */
  onValider: () => Promise<unknown>;
  /** Poste partagé ou mauvais compte : on peut partir sans valider. */
  onSeDeconnecter?: () => void | Promise<void>;
  enCours?: boolean;
  enErreur?: boolean;
}

/**
 * L'annonce de l'assistant à la première connexion — un écran plein, une
 * seule tâche : lire, puis continuer. Il REMPLACE l'application tant que
 * l'information n'a pas été validée (décision Rose 12/09/2026 : pas de mail
 * aux cabinets, l'application informe elle-même et garde la preuve).
 *
 * Aucun mot de la cuisine interne à l'écran : on dit ce que l'assistant fait,
 * où la demande est traitée, et ce qu'il ne lit jamais.
 *
 * Le bouton reste visible sur un téléphone : la barre d'action est collée en
 * bas de l'écran (même procédé que la saisie de l'assistant), le contenu
 * défile derrière.
 */
export function AnnonceAssistant({
  onValider,
  onSeDeconnecter,
  enCours,
  enErreur,
}: Props) {
  const [videoAbsente, setVideoAbsente] = useState(false);
  const titreRef = useRef<HTMLHeadingElement>(null);

  // Le focus arrive sur le titre : un lecteur d'écran lit d'abord de quoi il
  // s'agit, et le clavier repart de là.
  useEffect(() => {
    titreRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="annonce-assistant-titre"
      className="flex min-h-screen flex-col bg-gray-50"
    >
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between px-4 sm:h-16 sm:px-6">
          <Logo width={120} height={120} />
          {onSeDeconnecter && (
            <button
              type="button"
              onClick={() => void onSeDeconnecter()}
              className="text-xs text-neutral-500 hover:text-neutral-800 hover:underline"
            >
              Se déconnecter
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 pt-4 pb-2 sm:flex-none sm:px-6 sm:pt-6">
        <div className="flex items-center gap-3">
          <span className="bg-brand-50 text-brand-700 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10">
            <Sparkles className="h-5 w-5" />
          </span>
          <h1
            id="annonce-assistant-titre"
            ref={titreRef}
            tabIndex={-1}
            className="text-lg font-bold text-neutral-900 outline-none sm:text-xl"
          >
            Nouveau : demandez-le à l’assistant
          </h1>
        </div>

        {/* Version courte (décision Rose du 13/09/2026) : une phrase, le détail
            est dans la documentation. */}
        <p className="mt-3 text-sm leading-5 text-neutral-700 sm:mt-4 sm:leading-6">
          Écrivez ce que vous voulez, en français. Il consulte vos dossiers,
          lance vos automatisations, produit vos éditions, et vous demande
          confirmation avant d’agir.
        </p>

        {/* La démonstration : la vidéo si elle est là, trois exemples sinon.
            Jamais de lecteur externe (aucun traceur). */}
        <div className="mt-4 overflow-hidden rounded-xl border border-neutral-200 bg-white sm:mt-6">
          {videoAbsente ? (
            <div className="p-3 sm:p-4">
              <p className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
                Trois exemples de demandes
              </p>
              <ul
                aria-label="Exemples de demandes"
                className="mt-2 flex flex-wrap gap-2 sm:mt-3"
              >
                {EXEMPLES_SANS_VIDEO.map(exemple => (
                  <li
                    key={exemple}
                    className="rounded-full border border-neutral-200 bg-gray-50 px-3 py-1.5 text-sm leading-5 text-neutral-700"
                  >
                    {exemple}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <video
              controls
              preload="metadata"
              src={VIDEO_DEMO}
              poster={AFFICHE_DEMO}
              aria-label="Démonstration de l’assistant"
              className="aspect-video w-full bg-neutral-900"
              onError={() => setVideoAbsente(true)}
            />
          )}
        </div>

        <section
          aria-labelledby="annonce-assistant-donnees"
          className="border-primary-200 bg-primary-50 mt-4 rounded-xl border p-3 sm:mt-6 sm:p-4"
        >
          <h2
            id="annonce-assistant-donnees"
            className="text-primary-900 text-sm font-semibold"
          >
            Vos données (RGPD)
          </h2>
          <p className="text-primary-900 mt-1.5 text-sm leading-5 sm:mt-2 sm:leading-6">
            Traitées dans l’Union européenne. Jamais utilisées pour entraîner
            un modèle.
          </p>
        </section>

        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm sm:mt-4">
          <Link
            to={ROUTES.AIDE.ASSISTANT}
            className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
          >
            Lire la documentation →
          </Link>
          <Link
            to={ROUTES.LEGAL.PRIVACY}
            className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
          >
            Politique de confidentialité →
          </Link>
        </div>
      </main>

      {/* Sur téléphone, collée en bas (visible sans défiler) ; au bureau, sous
          les liens : un grand vide avant le bouton n'aide personne. */}
      <div className="sticky bottom-0 bg-gray-50/95 backdrop-blur-sm sm:static sm:bg-transparent sm:backdrop-blur-none">
        <div className="mx-auto flex w-full max-w-3xl flex-col items-start gap-1.5 px-4 py-3 sm:px-6 sm:py-4">
          <Button
            type="button"
            // L'échec est montré sous le bouton (enErreur) : rien à relever ici.
            onClick={() => void onValider().catch(() => undefined)}
            disabled={enCours}
            className="px-6"
          >
            J’ai compris, continuer
          </Button>
          {enErreur && (
            <p role="alert" className="text-error-600 text-xs">
              La validation n’a pas pu être enregistrée. Réessayez dans un
              instant.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
